import {
  ChangeEvent,
  DragEvent,
  FC,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { X } from "react-feather";
import * as Yup from "yup";
import {
  StyledHeader,
  StyledTitle,
  StyledBody,
  StyledDropzone,
  StyledFooterToolbar,
  StyledEmptyStateImageWrapper,
  StyledEmptyStateImg,
  StyledEmptyStateContainer,
} from "./style";
import { Modal } from "../../Common/Modal";
import { Nav, NavLink } from "../../Common/Nav/nav";
import { Col, Row } from "../../Common/Grid";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { Button } from "../../Common/Button";
import UploadImage from "../../../assets/images/upload.png";

interface IProps {
  show: boolean;
  onClose: () => any;
  onSubmit: (newMessage: any) => any;
}

const PROFILE_IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"];

const isAllowedProfileImage = (file: File): boolean =>
  PROFILE_IMAGE_MIME_TYPES.includes(file.type) &&
  !file.name.includes("..") &&
  file.name.split(".").length === 2;

const ChangePictureModal: FC<IProps> = ({ show, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [hightlight, setHightLight] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        Picture: Yup.mixed()
          .required(t("components.profile.pictureRequired"))
          .test(
            "fileType",
            t("components.profile.invalidPictureFile"),
            (value) => !value || isAllowedProfileImage(value as File)
          ),
      }),
    [t]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      Picture: null,
    },
    validationSchema,
    onSubmit,
  });

  const handleUploadFiles = useCallback(
    async (files: File[]) => {
      const file = files[0];

      if (!file || !isAllowedProfileImage(file)) {
        setHightLight(false);
        setImagePreview(null);
        setFileError(t("components.profile.invalidPictureFile"));
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      setFileError(null);
      formik.setFieldError("Picture", undefined);
      formik.setFieldValue("Picture", file);
      setImagePreview(URL.createObjectURL(file));
      setHightLight(false);
    },
    [formik, t]
  );

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setHightLight(true);
  };

  const onDragLeave = () => {
    setHightLight(false);
  };

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setHightLight(false);
      handleUploadFiles(Array.from(e.dataTransfer.files));
    },
    [handleUploadFiles]
  );

  const onSelectedFile = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      handleUploadFiles(Array.from(event.currentTarget.files || []));
    },
    [handleUploadFiles]
  );

  const handleOnClose = useCallback(
    (e?: any) => {
      e && e.preventDefault();
      onClose();
      setHightLight(false);
      setImagePreview(null);
      setFileError(null);
      formik.resetForm();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [formik, onClose]
  );

  return (
    <Modal
      show={show}
      onClose={handleOnClose}
      size="sm"
      closeOnClickBackdrop={false}
    >
      <StyledHeader backgroundColor={"#002E6B"}>
        <StyledTitle>
          {t("components.profile.changeProfilePicture")}
        </StyledTitle>
        <Nav customStyle="icon">
          <NavLink path="#!" onClick={handleOnClose}>
            <X />
          </NavLink>
        </Nav>
      </StyledHeader>
      <StyledBody>
        <form onSubmit={formik.handleSubmit}>
          <div onDragOver={onDragOver}>
            <StyledDropzone
              hightlight={hightlight}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            />

            <Row>
              <input
                ref={fileInputRef}
                id="file"
                name="file"
                type="file"
                accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                onChange={onSelectedFile}
                style={{ display: "none" }}
              />
              <Col marginTop={10} textAlign="center">
                {imagePreview ? (
                  <img src={imagePreview} alt="" />
                ) : (
                  <EmptyState
                    onUploadClick={() => fileInputRef.current?.click()}
                  />
                )}
                {(fileError || formik.errors.Picture) && (
                  <p style={{ color: "#c0392b", marginTop: "0.75rem" }}>
                    {fileError || formik.errors.Picture}
                  </p>
                )}
              </Col>
            </Row>
          </div>
          <Row>
            <StyledFooterToolbar marginTop={10} color={"#002E6B"}>
              <Button type="submit" className="sendBtt">
                {t("components.profile.upload")}
              </Button>
            </StyledFooterToolbar>
          </Row>
        </form>
      </StyledBody>
    </Modal>
  );
};

export default ChangePictureModal;

type EmptyStateProps = {
  onUploadClick: () => any;
};

const EmptyState: React.FC<EmptyStateProps> = ({ onUploadClick }) => {
  const { t } = useTranslation();
  return (
    <StyledEmptyStateContainer>
      <StyledEmptyStateImageWrapper>
        <StyledEmptyStateImg src={UploadImage} alt="upload" />
        <div>{t("components.profile.dndMessage")}</div>
      </StyledEmptyStateImageWrapper>
      <p style={{ marginTop: "0.5rem" }}>{t("components.profile.or")}</p>
      <Button iconButton variant="texted" onClick={onUploadClick}>
        {t("components.profile.uploadPicture")}
      </Button>
    </StyledEmptyStateContainer>
  );
};
